import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { toastAction } from '@app/shared/operators/toast-action.operator';
import { UserApiService } from '../../../api/user-api.service';
import {
  User,
  UserGeneralSettings,
  UserLanguage,
  UserLineChartSettings,
  UserSetting,
  UserSettings,
  UserTableLayoutSettings,
} from '../../../models/user.models';
import {
  GetUser,
  GetUserSettings,
  ResendEmailVerification,
  SetCOAToggle,
  SetPgGoalsIncludePausedCampaignsInMetrics,
  SetUserSettings,
  SetUserTableSettings,
  UpdateUser,
} from './user-state.actions';
import { USER_STATE_TOKEN, UserStateModel } from './user-state.models';

@Injectable()
@State<UserStateModel>({
  name: USER_STATE_TOKEN,
  defaults: {},
})
export class UserState {
  @Selector()
  static userId(user: UserStateModel): number | undefined {
    return user.details?.userId;
  }

  @Selector()
  static user(user: UserStateModel): User | undefined {
    return user.details;
  }

  @Selector()
  static ssoUser(user: UserStateModel): boolean {
    return !!user.details?.isAuth0User;
  }

  @Selector()
  static userLocale(user: UserStateModel): UserLanguage {
    return user.details.lang;
  }

  @Selector([UserState.user])
  static emailVerified(user: User | undefined): boolean {
    return user?.emailVerified !== false;
  }

  @Selector()
  static settings(user: UserStateModel): UserSettings | undefined {
    return user.settings;
  }

  @Selector()
  static metricChart(user: UserStateModel): UserLineChartSettings | undefined {
    return user.settings?.[UserSetting.LineChart];
  }

  @Selector([UserState.settings])
  static pgGoalsUseUserCurrencySettings(settings: UserSettings) {
    return settings?.[UserSetting.PgGoalsUseUserCurrency] || {};
  }

  static setting<T extends UserSetting>(setting: T, settingDefaults?: Partial<UserSettings[T]>) {
    return createSelector([UserState.settings], (settings: UserSettings): UserSettings[T] | undefined => {
      let settingValue = settings?.[setting];

      if (settingDefaults) {
        settingValue = { ...settingValue };

        Object.entries(settingDefaults).forEach(([key, value]) => (settingValue[key] ??= value));
      }

      return settingValue;
    });
  }

  @Selector()
  static isSysAdmin(user: UserStateModel): boolean {
    return !!user.details?.sysAdmin;
  }

  @Selector([UserState.setting(UserSetting.General)])
  static selectedCOA(setting: UserGeneralSettings): boolean {
    const coa = setting?.coa;
    return !!coa;
  }

  @Selector([UserState.setting(UserSetting.General)])
  static pgGoalsIncludePausedCampaignsInMetrics(setting: UserGeneralSettings): boolean {
    return setting?.pgGoalsIncludePausedCampaignsInMetrics;
  }

  constructor(
    private ua: UserApiService,
    private store: Store,
  ) {}

  @Action(GetUser)
  getUser({ setState }: StateContext<UserStateModel>): Observable<unknown> {
    return this.ua.getUserById(0).pipe(
      tap((user: User) => {
        window.adspert_data.user = user;
        return setState(patch({ details: user }));
      }),
    );
  }

  @Action(GetUserSettings)
  getUserSettings({ setState }: StateContext<UserStateModel>): Observable<unknown> {
    return this.ua.getUserSettings().pipe(tap((settings: UserSettings) => setState(patch({ settings }))));
  }

  @Action(UpdateUser)
  updateUser({ setState }: StateContext<UserStateModel>, { update }: UpdateUser): Observable<unknown> {
    const id = update.userId || this.store.selectSnapshot(UserState.userId);
    const userId = this.store.selectSnapshot(UserState.userId);
    return this.ua.updateUser(id, update).pipe(
      tap(() => {
        if (id === userId) {
          setState(patch({ details: patch(update) }));
        }
      }),
    );
  }

  @Action(SetUserTableSettings)
  setUserTableSettings(
    { dispatch, getState }: StateContext<UserStateModel>,
    { tableLayout }: SetUserTableSettings,
  ): Observable<unknown> {
    const { settings } = getState();

    const updatedTableLayout = {
      ...settings?.[UserSetting.TableLayout],
      ...tableLayout,
    } as UserTableLayoutSettings;

    return dispatch(new SetUserSettings({ [UserSetting.TableLayout]: updatedTableLayout }));
  }

  @Action(SetPgGoalsIncludePausedCampaignsInMetrics)
  setPgGoalsIncludePausedCampaignsInMetrics(
    { dispatch, getState },
    { value }: SetPgGoalsIncludePausedCampaignsInMetrics,
  ): Observable<unknown> {
    const { settings } = getState();

    const updated: UserGeneralSettings = {
      ...settings?.[UserSetting.General],
      pgGoalsIncludePausedCampaignsInMetrics: value,
    };

    return dispatch(new SetUserSettings({ [UserSetting.General]: updated }));
  }

  @Action(SetCOAToggle)
  SetCOAToggle({ dispatch, getState }, { value }: SetCOAToggle): Observable<unknown> {
    const { settings } = getState();

    const updated: UserGeneralSettings = {
      ...settings?.[UserSetting.General],
      coa: value,
    };

    return dispatch(new SetUserSettings({ [UserSetting.General]: updated }));
  }

  @Action(SetUserSettings)
  setUserSetting(
    { setState }: StateContext<UserStateModel>,
    { updatedSettings, direct }: SetUserSettings,
  ): Observable<unknown> {
    const patchSettings = () => setState(patch({ settings: patch(updatedSettings) }));

    if (direct) {
      patchSettings();
    }

    return this.ua.putUserSettings(updatedSettings).pipe(
      tap(() => {
        if (!direct) {
          patchSettings();
        }
      }),
      defaultToastError(),
      catchError(() => of(null)),
    );
  }

  @Action(ResendEmailVerification)
  resendEmailVerification(): Observable<unknown> {
    return this.ua.resendEmailVerification().pipe(toastAction('common.email-verification.success'));
  }
}
